import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import { Mail, RefreshCw, Send, ShoppingCart, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "log", label: "Email Log" },
  { key: "abandoned", label: "Abandoned Carts" },
  { key: "stock", label: "Back-in-Stock" },
];

export default function AdminEmail() {
  const client = adminClient();
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("log");
  const [rows, setRows] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(() => { client.get("/admin/email/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  const loadRows = useCallback(() => {
    setLoading(true);
    const url = tab === "log" ? "/admin/email/log" : tab === "abandoned" ? "/admin/email/abandoned" : "/admin/email/stock";
    const key = tab === "log" ? "log" : tab === "abandoned" ? "carts" : "notifications";
    client.get(url).then(({ data }) => setRows(data[key] || [])).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const processNow = async () => { try { const { data } = await client.post("/admin/email/process"); toast.success(`Processed: ${JSON.stringify(data.processed)}`); loadStatus(); loadRows(); } catch { toast.error("Failed"); } };
  const sendTest = async () => { if (!testTo) { toast.error("Enter an email"); return; } try { const { data } = await client.post("/admin/email/test", { to: testTo }); toast.success(`Test: ${data.result.status}`); loadStatus(); } catch { toast.error("Failed"); } };

  const provider = status?.provider;
  const counts = status?.counts || {};

  return (
    <div data-testid="admin-email">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="text-xl font-black font-display">Email Center</h2>
        <div className="flex gap-2 items-center">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@email.com" data-testid="email-test-to" className="border border-black/15 px-3 py-2 text-sm" />
          <button onClick={sendTest} data-testid="email-test-send" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-black/15 px-3 py-2 hover:bg-taven-gray"><Send className="w-3.5 h-3.5" /> Send Test</button>
          <button onClick={processNow} data-testid="email-process" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-black text-white px-3 py-2 hover:bg-taven-orange"><RefreshCw className="w-3.5 h-3.5" /> Process Now</button>
        </div>
      </div>

      <div className={`p-4 mb-5 border ${provider?.configured ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
        <p className="text-sm font-bold flex items-center gap-2"><Mail className="w-4 h-4" />
          Provider: <span className="uppercase">{provider?.provider || "none"}</span> · {provider?.configured ? "Configured & ready" : "Not configured — set EMAIL_PROVIDER + API key in environment. Emails queue as ‘pending’ until then."}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[["Sent", counts.sent, Mail], ["Pending", counts.pending_provider, Mail], ["Failed", counts.failed, Mail], ["Abandoned", counts.abandoned_active, ShoppingCart], ["Stock Waiting", counts.stock_waiting, Bell]].map(([l, v, I]) => (
          <div key={l} className="bg-white border border-black/10 p-4"><I className="w-4 h-4 text-taven-gold" /><p className="text-2xl font-black mt-1">{v ?? "—"}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p></div>
        ))}
      </div>

      <div className="flex gap-2 mb-3">
        {TABS.map((t) => <button key={t.key} onClick={() => setTab(t.key)} data-testid={`email-tab-${t.key}`} className={`text-xs font-bold uppercase tracking-wider px-3 py-2 ${tab === t.key ? "bg-taven-black text-white" : "bg-taven-gray"}`}>{t.label}</button>)}
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></div> :
          rows.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No records.</p> :
            <table className="w-full text-sm">
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-black/5">
                    {tab === "log" && <><td className="px-3 py-2 font-semibold">{r.type}</td><td className="px-3 py-2">{r.to}</td><td className="px-3 py-2 text-muted-foreground truncate max-w-[240px]">{r.subject}</td><td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${r.status === "sent" ? "bg-green-50 text-green-700" : r.status === "failed" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>{r.status}</span></td><td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td></>}
                    {tab === "abandoned" && <><td className="px-3 py-2">{r.email}</td><td className="px-3 py-2">{(r.items || []).length} item(s)</td><td className="px-3 py-2 font-bold">${Number(r.subtotal || 0).toFixed(2)}</td><td className="px-3 py-2"><span className="text-[10px] font-bold uppercase">{r.status}</span>{r.reminded && <span className="text-[10px] ml-2 text-taven-gold">reminded</span>}</td><td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleString()}</td></>}
                    {tab === "stock" && <><td className="px-3 py-2">{r.email}</td><td className="px-3 py-2">{r.title}</td><td className="px-3 py-2"><span className="text-[10px] font-bold uppercase">{r.status}</span></td><td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleString()}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
    </div>
  );
}
